// GAEB-/Compliance-Schutz für KI-Textbearbeitung.
//
// Historie: Diese Muster lebten als Modul-Konstanten in server/routes.ts und
// schützten dort den PDF-Optimierungspfad und den freien Rewrite-Endpunkt
// (/api/ai/rewrite). Für die KI-Formulierungshilfe je Position/Titel in der
// Angebotsmaske braucht auch der CLIENT dieselben Muster (Nachprüfung des
// Vorschlags VOR der Übernahme) — deshalb hierher nach shared/ gezogen.
// server/routes.ts re-exportiert applyPdfAiSafeguards/getPdfOptimizationSkipReason
// unverändert, damit bestehende Importe (tests/ai-rewrite-route.test.ts) und
// die Serverpfade identisch weiterlaufen. EINE Quelle, keine Duplikate.

export const AI_CRITICAL_LINE_PATTERNS: readonly RegExp[] = [
  /^Fabrikat der Planung\b.*$/i,
  /^Angebotenes Fabrikat\b.*$/i,
  /^Angebotener Typ\b.*$/i,
  /\boder gleichwertig\b/i,
];

export const AI_TECHNICAL_LINE_PATTERNS: readonly RegExp[] = [
  /\bDIN\s+EN\b/i,
  /\b\d+(?:[.,]\d+)?\s*(?:V|kV|W|kW|A|mA|Hz|°C|mm|cm|Ah)\b/i,
  /\b(?:24V-Stromschleife|24V|230V|400V|50\/60\s*Hz|CPS\s*\d+|IP\s*\d+|E30|E90)\b/i,
];

export const AI_ARTIFACT_PATTERNS: readonly RegExp[] = [
  /\bmso-tab-count\b/i,
  /(?:\r?\n){3,}/,
  /[•·].{0,4}[•·]/,
];

export const normalizeAiLine = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[•·]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const getPdfOptimizationSkipReason = (
  description: string,
  aiMode: 'light' | 'max',
): string | null => {
  const trimmed = description.trim();

  if (trimmed.length < 50) {
    return 'Text zu kurz';
  }

  const hasArtifacts = AI_ARTIFACT_PATTERNS.some((pattern) => pattern.test(trimmed));
  const hasStructuredLines =
    trimmed.includes('\n') ||
    trimmed.includes('•') ||
    /:\s*\n/.test(trimmed);

  if (aiMode === 'max') {
    if (hasArtifacts || trimmed.length >= 300) {
      return null;
    }

    if (trimmed.length >= 180 && hasStructuredLines) {
      return null;
    }

    return 'Für KI-Max zu kurz/einfach';
  }

  if (hasArtifacts || trimmed.length >= 140) {
    return null;
  }

  if (trimmed.length >= 100 && hasStructuredLines) {
    return null;
  }

  return 'Keine KI-Optimierung nötig';
};

// Stellt sicher, dass geschützte Pflichtzeilen (GAEB-Compliance-Felder wie
// "Fabrikat der Planung", "oder gleichwertig" sowie im aiMode 'max' technische
// Angaben) nach der KI-Bearbeitung weiterhin im Text vorkommen. Fehlende Zeilen
// werden an den bearbeiteten Text angehängt (Reparatur statt Verwerfen).
export const applyPdfAiSafeguards = (
  originalText: string,
  optimizedText: string,
  aiMode: 'light' | 'max',
): string => {
  const sourceLines = originalText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const protectedLines = sourceLines.filter((line) => {
    if (AI_CRITICAL_LINE_PATTERNS.some((pattern) => pattern.test(line))) {
      return true;
    }

    return aiMode === 'max' && AI_TECHNICAL_LINE_PATTERNS.some((pattern) => pattern.test(line));
  });

  if (protectedLines.length === 0) {
    return optimizedText.trim();
  }

  const dedupedProtectedLines = Array.from(
    new Map(protectedLines.map((line) => [normalizeAiLine(line), line])).values(),
  );

  const normalizedOptimized = normalizeAiLine(optimizedText);
  const missingLines = dedupedProtectedLines.filter((line) => {
    const normalizedLine = normalizeAiLine(line);
    return normalizedLine.length > 0 && !normalizedOptimized.includes(normalizedLine);
  });

  if (missingLines.length === 0) {
    return optimizedText.trim();
  }

  const prefixWithBullet = aiMode === 'max';
  const additions = missingLines.map((line) => {
    if (!prefixWithBullet) {
      return line;
    }
    return line.startsWith('•') ? line : `• ${line}`;
  });

  return `${optimizedText.trim()}\n${additions.join('\n')}`.trim();
};
